import React, { useState, useEffect } from "react";
import { Box, Typography, Button, Alert } from "@mui/material";
import {
  Email as EmailIcon,
  OpenInNew as OpenInNewIcon,
} from "@mui/icons-material";

const MailhogViewer = () => {
  const [iframeError, setIframeError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Version identifier for debugging
  console.log("MailhogViewer - Component version: 2024-01-08-v2");

  // Use different URLs based on environment
  const getMailhogUrl = () => {
    const hostname = window.location.hostname;
    console.log("MailhogViewer - Current hostname:", hostname);
    console.log("MailhogViewer - Full window.location:", window.location.href);

    // Local development
    if (hostname === "localhost" || hostname === "127.0.0.1") {
      return "http://localhost:8025";
    }

    // Dev environment - Use direct MailHog service
    if (hostname.includes("dev")) {
      const mailhogUrl = "https://mailhog.2apigennevinfo.com";
      console.log("MailhogViewer - Using dev MailHog direct URL:", mailhogUrl);
      return mailhogUrl;
    }

    // Staging environment - Use direct MailHog service
    if (hostname.includes("stage") || hostname.includes("staging")) {
      const mailhogUrl = "https://mailhog.2apigennevinfo.com";
      console.log(
        "MailhogViewer - Using staging MailHog direct URL:",
        mailhogUrl
      );
      return mailhogUrl;
    }

    // Default fallback
    return "http://localhost:8025";
  };

  const mailhogUrl = getMailhogUrl();
  console.log("MailhogViewer - Final mailhogUrl:", mailhogUrl);
  console.log(
    "MailhogViewer - mailhogUrl === expected:",
    mailhogUrl === "https://mailhog.2apigennevinfo.com"
  );

  const handleIframeError = () => {
    console.error("MailhogViewer - Iframe failed to load:", mailhogUrl);
    setIframeError(true);
  };

  const handleIframeLoad = () => {
    console.log("MailhogViewer - Iframe loaded successfully:", mailhogUrl);
    setIframeError(false);
    setIsLoading(false);
  };

  // Set a timeout to detect if iframe fails to load
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (isLoading) {
        console.warn("MailhogViewer - Iframe loading timeout:", mailhogUrl);
        setIframeError(true);
        setIsLoading(false);
      }
    }, 10000); // 10 second timeout

    return () => clearTimeout(timeout);
  }, [isLoading, mailhogUrl]);

  const openInNewTab = () => {
    // Get fresh URL to avoid any potential issues with the mailhogUrl variable
    const freshUrl = getMailhogUrl();
    console.log("MailhogViewer - Opening in new tab with URL:", freshUrl);
    window.open(freshUrl, "_blank");
  };

  if (iframeError) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "400px",
          gap: 2,
          p: 3,
        }}
      >
        <EmailIcon sx={{ fontSize: 48, color: "primary.main" }} />
        <Typography variant="h5" component="h2">
          Mailhog Email Testing
        </Typography>
        <Alert severity="warning" sx={{ maxWidth: 600 }}>
          Unable to load Mailhog interface in this view. This might be due to
          CORS restrictions or network issues.
        </Alert>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            fontFamily: "monospace",
            backgroundColor: "#f5f5f5",
            p: 1,
            borderRadius: 1,
          }}
        >
          URL: {mailhogUrl}
        </Typography>
        <Typography variant="body1" color="text.secondary" textAlign="center">
          Click the button below to open Mailhog in a new tab:
        </Typography>
        <Button
          variant="contained"
          startIcon={<OpenInNewIcon />}
          onClick={openInNewTab}
          size="large"
        >
          Open Mailhog in New Tab
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          p: 2,
          borderBottom: 1,
          borderColor: "divider",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          backgroundColor: "background.paper",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <EmailIcon sx={{ color: "primary.main" }} />
          <Typography variant="h6" component="h2">
            Email Testing - Mailhog
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<OpenInNewIcon />}
          onClick={openInNewTab}
          size="small"
        >
          Open in New Tab
        </Button>
      </Box>

      <Box sx={{ flex: 1, position: "relative" }}>
        {isLoading && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(255, 255, 255, 0.8)",
              zIndex: 1,
            }}
          >
            <Typography variant="body1" color="text.secondary">
              Loading Mailhog interface...
            </Typography>
          </Box>
        )}
        <iframe
          src={mailhogUrl}
          style={{
            width: "100%",
            height: "100%",
            border: "none",
          }}
          title="Mailhog Email Testing Interface"
          onError={handleIframeError}
          onLoad={handleIframeLoad}
        />
      </Box>
    </Box>
  );
};

export default MailhogViewer;
